import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Index';
import { PortableText } from '@portabletext/react';
import { CalendarIcon, Clock, Mail, Share2, Tag } from 'lucide-react';
import { useBlogPost, useBlogPosts } from '@/hooks/useSanityData';
import { PortableTextBlock } from '@portabletext/types';
import { useEffect, useState } from 'react';
import Loader from '@/components/ui/Loader';

interface BlogPostData {
	id: string;
	title: string;
	excerpt: string;
	content: PortableTextBlock[];
	imageUrl: string;
	published: string;
	readTime: number;
	category: string;
	author: string;
}

const BlogPost = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [isContentReady, setIsContentReady] = useState(false);

	const { data: post, isLoading: isPostLoading } = useBlogPost(id || '');
	const { data: allPosts, isLoading: isAllPostsLoading } = useBlogPosts();

	useEffect(() => {
		// Add a slight delay to ensure content is fully loaded and rendered
		if (!isPostLoading && !isAllPostsLoading && post) {
			const timer = setTimeout(() => {
				setIsContentReady(true);
			}, 300);

			return () => clearTimeout(timer);
		}
	}, [isPostLoading, isAllPostsLoading, post]);

	if (!id) {
		navigate('/not-found');
		return null;
	}

	if (isPostLoading || isAllPostsLoading || !isContentReady) {
		return <Loader />;
	}

	if (!post) {
		return (
			<Layout>
				<div className='min-h-screen flex items-center justify-center'>
					Blog post not found
				</div>
			</Layout>
		);
	}

	// Format the published date
	const formattedPost: BlogPostData = {
		...post,
		published: new Date(post.published).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		}),
	};

	// Find related articles with the same category
	const relatedArticles =
		allPosts
			?.filter((p) => p.id !== id && p.category === post.category)
			.slice(0, 2)
			.map((p) => ({
				...p,
				published: new Date(p.published).toLocaleDateString('en-US', {
					year: 'numeric',
					month: 'long',
					day: 'numeric',
				}),
			})) || [];

	const handleShare = () => {
		if (navigator.share) {
			navigator
				.share({
					title: formattedPost.title,
					text: formattedPost.excerpt,
					url: window.location.href,
				})
				.catch(console.error);
		} else {
			// Fallback to copying to clipboard
			navigator.clipboard
				.writeText(window.location.href)
				.then(() => {
					alert('Link copied to clipboard!');
				})
				.catch(console.error);
		}
	};

	const handleContactAuthor = () => {
		window.location.href = `mailto:${formattedPost.author}?subject=Re: ${formattedPost.title}`;
	};

	return (
		<Layout>
			<div className='min-h-screen flex flex-col'>
				<main className='flex-grow pt-24 pb-12'>
					<div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8'>
						{/* Hero Image */}
						<div className='w-full mb-8 transition-opacity duration-500'>
							<div className='relative overflow-hidden rounded-xl shadow-lg h-[400px]'>
								<img
									src={formattedPost.imageUrl}
									alt={formattedPost.title}
									className='w-full h-full object-cover object-center'
									loading='eager'
								/>
							</div>
						</div>

						<div className='transition-all duration-500'>
							{/* Category Tag */}
							<div className='mb-4'>
								<span className='inline-block bg-blog-primary px-3 py-1 text-white text-xs font-semibold rounded-full'>
									{formattedPost.category}
								</span>
							</div>

							{/* Title */}
							<h1 className='text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-balance'>
								{formattedPost.title}
							</h1>

							{/* Author and Publication Info */}
							<div className='flex flex-wrap gap-4 mb-8 text-sm text-gray-600'>
								<div className='flex items-center'>
									<CalendarIcon className='w-4 h-4 mr-2' />
									{formattedPost.published}
								</div>
								<div className='flex items-center'>
									<Clock className='w-4 h-4 mr-2' />
									{formattedPost.readTime} min read
								</div>
								<div className='flex items-center'>
									<Tag className='w-4 h-4 mr-2' />
									{formattedPost.category}
								</div>
							</div>

							{/* Share and Contact Buttons */}
							<div className='flex gap-4 mb-8'>
								<button
									onClick={handleShare}
									className='flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors'
								>
									<Share2 className='w-4 h-4 mr-2' />
									Share
								</button>
								<button
									onClick={handleContactAuthor}
									className='flex items-center px-4 py-2 bg-blog-primary text-white hover:bg-blog-primary/90 rounded-lg transition-colors'
								>
									<Mail className='w-4 h-4 mr-2' />
									Contact Author
								</button>
							</div>

							{/* Content */}
							<div className='prose prose-lg max-w-none'>
								<p className='text-lg text-gray-700 mb-6 leading-relaxed'>
									{formattedPost.excerpt}
								</p>

								{/* Render Portable Text content */}
								{formattedPost.content && (
									<div className='mb-6'>
										<PortableText
											value={formattedPost.content}
											components={{
												block: {
													h1: ({ children }) => (
														<h1 className='text-3xl font-bold my-4'>
															{children}
														</h1>
													),
													h2: ({ children }) => (
														<h2 className='text-2xl font-bold my-3'>
															{children}
														</h2>
													),
													h3: ({ children }) => (
														<h3 className='text-xl font-bold my-2'>
															{children}
														</h3>
													),
													h4: ({ children }) => (
														<h4 className='text-lg font-semibold my-2'>
															{children}
														</h4>
													),
													normal: ({ children }) => (
														<p className='mb-4'>{children}</p>
													),
													blockquote: ({ children }) => (
														<blockquote className='pl-4 border-l-4 border-gray-300 italic my-4'>
															{children}
														</blockquote>
													),
												},
												list: {
													bullet: ({ children }) => (
														<ul className='list-disc pl-6 mb-4'>{children}</ul>
													),
													number: ({ children }) => (
														<ol className='list-decimal pl-6 mb-4'>
															{children}
														</ol>
													),
												},
												marks: {
													strong: ({ children }) => (
														<strong className='font-bold'>{children}</strong>
													),
													em: ({ children }) => (
														<em className='italic'>{children}</em>
													),
													link: ({ value, children }) => {
														const target = (value?.href || '').startsWith(
															'http',
														)
															? '_blank'
															: undefined;
														return (
															<a
																href={value?.href}
																target={target}
																rel={
																	target === '_blank'
																		? 'noopener noreferrer'
																		: undefined
																}
																className='text-blue-600 hover:underline'
															>
																{children}
															</a>
														);
													},
												},
											}}
										/>
									</div>
								)}
							</div>

							{/* Related Articles */}
							{relatedArticles && relatedArticles.length > 0 && (
								<div className='mt-12 border-t border-gray-200 pt-8'>
									<h2 className='text-2xl font-bold mb-6'>Related Articles</h2>
									<div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
										{relatedArticles.map((article) => (
											<a
												key={article.id}
												href={`/blog/${article.id}`}
												className='group'
											>
												<div className='bg-white rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300'>
													<div className='aspect-w-16 aspect-h-9 bg-gray-200'>
														<img
															src={article.imageUrl}
															alt={article.title}
															className='w-full h-48 object-cover object-center'
														/>
													</div>
													<div className='p-4'>
														<span className='inline-block bg-blog-primary px-2 py-1 text-white text-xs font-semibold rounded-full mb-2'>
															{article.category}
														</span>
														<h3 className='text-lg font-semibold text-gray-900 mb-2 group-hover:text-blog-primary transition-colors duration-200'>
															{article.title}
														</h3>
														<p className='text-sm text-gray-600 line-clamp-2 mb-3'>
															{article.excerpt}
														</p>
														<div className='flex justify-between items-center text-xs text-gray-500'>
															<span>{article.published}</span>
															<span>{article.readTime} min read</span>
														</div>
													</div>
												</div>
											</a>
										))}
									</div>
								</div>
							)}
						</div>
					</div>
				</main>
			</div>
		</Layout>
	);
};

export default BlogPost;
